import { useEffect, useContext, useRef, useCallback } from "react";
import { BaseWidget } from "../widgets";
import { WidgetBrowser } from "./WidgetBrowser";
import { Canvas } from "../components/Canvas";
import { EditorContext } from "../context/EditorContext";
import { TreeView } from "./TreeView";
import { ContextMenu } from "./ContextMenu";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WidgetsIcon from '@mui/icons-material/Widgets';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings';
import { WidgetControlsPanel } from "./WidgetControl";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import SavedNotice from "../../../common/SavedNotice";
import { MiniMap } from "./MiniMap";
import { SavedIcon } from "../../../../StateManagment/SavedIcon";

/**
 * Editor component for building live report templates.
 * @returns 
 */
export const Editor = () => {

    const [saved] = useContext(SavedIcon);
    const editorCtx = useContext(EditorContext);

    if (!editorCtx) {
        throw new Error("Editor must be used within an EditorContextProvider");
    }

    const { 
        widgets,
        setWidgets,
        addWidget,
        activeWidgetId, setActiveWidgetId,
        setHoveredWidgetId,
        undo, redo, historyIndex, canUndo, canRedo,
        getWidgetById,
        instantiateWidget,
        deleteWidget,
        controlsFlashToken,
        duplicateWidget,
        activeTab, setActiveTab,
        scheduleSave
    } = editorCtx;

    /** @type {React.MutableRefObject<HTMLDivElement | null>} */
    const controlsPanelRef = useRef(null);

    /** @type {React.MutableRefObject<HTMLDivElement | null>} */
    const canvasAreaRef = useRef(null);

    /* Set by a click in the widget list: the added widget becomes active, but
       the panel stays on 'Widgets' so the next widget is one click away
       (inbox #325). One-shot; any later (re)select opens the settings again. */
    const keepWidgetsTabRef = useRef(false);

    /**
     * Auto-switch to settings tab when a widget is selected. Also on a reselect
     * of the active widget (controlsFlashToken), so a widget added from the
     * list opens its settings once you click it on the canvas.
     */
    useEffect(() => {
        if (activeWidgetId === null) return;
        if (keepWidgetsTabRef.current) {
            keepWidgetsTabRef.current = false;
            return;
        }
        setActiveTab('settings');
    }, [activeWidgetId, controlsFlashToken, setActiveTab]);

    const deselectWidget = useCallback(() => {
        if (activeWidgetId !== null) {
            setActiveWidgetId(null); // Will also reset setActiveTab()
        }
    }, [activeWidgetId, setActiveWidgetId]);

    /**
     * Handle 'clicking outside' to deselect items
     */
    useEffect(() => {

        /** @argument {any} event */
        const handleClickOutside = (event) => {
            if (event.button !== 0) return; // Only respond to left-clicks

            if (!controlsPanelRef.current?.contains(event.target) &&
                !canvasAreaRef.current?.contains(event.target)
            ) {
                deselectWidget();
            }
        };

        document.addEventListener('mouseup', handleClickOutside);

        return () => {
            document.removeEventListener('mouseup', handleClickOutside);
        };
    }, [deselectWidget, activeTab]);

    /**
     * Exit the active widget / controls by pressing 'escape'
     */
    useEffect(() => {
        /** @argument {KeyboardEvent} event */
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                deselectWidget();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [deselectWidget]);

    /**
     * Handle control value changes
     * @param {string} controlKey - Name of the control being updated
     * @param {any} newValue - New value for the control
     */
    const handleControlChange = (controlKey, newValue) => {
        if (activeWidgetId === null) return;
        
        /* Update the widget's setting */
        getWidgetById(activeWidgetId)?.setSetting(controlKey, newValue);

        /* Trigger re-render by creating a new array reference */
        setWidgets && setWidgets([...widgets]);

        /* Persist without needing to deselect the widget (debounced). */
        scheduleSave && scheduleSave();
    };

    /* Aliases / helpers */
    const activeWidget = getWidgetById(activeWidgetId);
    
    const rootNode = widgets.find(w => w.parent === null && !w.deleted); // @todo store root ID in db? 

    if (!rootNode) {
        return <p>Error: Root node not found</p>;
    }

    return (
        <div style={{ 
            display: 'flex', 
            gap: '16px', 
            width: '100%' 
        }}>

            <div 
                className="report-builder-controls-panel" 
                ref={controlsPanelRef}
            >
                {/* Always-visible toolbar: save status + undo/redo */}
                <div className="editor-toolbar">
                    <SavedNotice />
                    <div className="editor-toolbar-actions">
                        <button
                            className="editor-toolbar-btn"
                            onClick={() => { undo(); }}
                            disabled={!canUndo || saved === 'flex'}
                            title="Ongedaan maken"
                        >
                            <UndoIcon />
                            <span>{historyIndex}</span>
                        </button>
                        <button
                            className="editor-toolbar-btn"
                            onClick={() => { redo(); }}
                            disabled={!canRedo || saved === 'flex'}
                            title="Opnieuw"
                        >
                            <RedoIcon />
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="editor-tab-navigation">
                    {/* <button 
                        className={`editor-tab ${activeTab === 'actions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('actions')}
                        title="Acties"
                    >
                        <MenuIcon />
                        <span className="editor-tab-label">Acties</span>
                    </button> */}
                    <button 
                        className={`editor-tab ${activeTab === 'widgets' ? 'active' : ''}`}
                        onClick={() => setActiveTab('widgets')}
                        title="Widgets"
                    >
                        <WidgetsIcon />
                        <span className="editor-tab-label">Widgets</span>
                    </button>
                    <button 
                        className={`editor-tab ${activeTab === 'structure' ? 'active' : ''}`}
                        onClick={() => setActiveTab('structure')}
                        title="Structuur"
                    >
                        <AccountTreeIcon />
                        <span className="editor-tab-label">Structuur</span>
                    </button>
                    {activeWidgetId !== null && (
                        <button 
                            className={`editor-tab ${activeTab === 'settings' ? 'active' : ''}`}
                            onClick={() => setActiveTab('settings')}
                            title="Instellingen"
                        >
                            <SettingsIcon /> {/* {activeWidget.getIcon()} */}
                            <span className="editor-tab-label">Widget</span>
                        </button>
                    )}
                </div>

                {/* Tab Content */}
                <div className="editor-tab-content">

                    {/* Widgets Tab */}
                    {activeTab === 'widgets' && (
                        <div className="editor-tab-panel">
                            <p className="editor-panel-description">
                                Sleep een widget naar het canvas, of klik om toe te voegen.
                            </p>
                            <WidgetBrowser onClickWidget={(WidgetClass) => {
                                const newWidget = instantiateWidget(WidgetClass);
                                
                                /* Add widget, default to bottom of root element */
                                keepWidgetsTabRef.current = true;
                                addWidget(newWidget, rootNode.id, 999);
                                // addWidget falls back to 'structure' for an id not yet in `widgets`.
                                setActiveTab('widgets');
                                // onSave([...widgets, newWidget]); // Trigger a save action
                                // editorCtx.save();
                            }} />
                        </div>
                    )}

                    {/* Structure Tab */}
                    {activeTab === 'structure' && (
                        <div className="editor-tab-panel">
                            <p className="editor-panel-description">
                                Klik om een widget te selecteren.
                            </p>
                            <MiniMap 
                                widgets={widgets} 
                                onMouseOver={widget => {
                                    setHoveredWidgetId(widget?.id || null);
                                }}
                                onClickWidget={widget => {
                                    setActiveWidgetId(widget.id);
                                    setHoveredWidgetId(null);
                                }}
                            />
                            <TreeView 
                                widgets={widgets} 
                                onMouseOver={widget => {
                                    setHoveredWidgetId(widget?.id || null);
                                }}
                                onClickWidget={widget => {
                                    setActiveWidgetId(widget.id);
                                    setHoveredWidgetId(null);
                                }}
                                onDeleteWidget={widget => {
                                    deleteWidget(widget.id);
                                }}
                            />
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 'settings' && activeWidget && (
                        <div className="editor-tab-panel">
                            <div className="editor-panel-header">
                                <h3 className="editor-panel-title">
                                    {activeWidget.getIcon()} {activeWidget.getName() || '<UNNAMED WIDGET>'}
                                </h3>
                                <div className="editor-panel-actions">
                                    <button
                                        className="editor-close-button"
                                        onClick={() => activeWidgetId && duplicateWidget(activeWidgetId)}
                                        title="Dupliceren"
                                        disabled={!activeWidget || activeWidget.parent === null}
                                    >
                                        <ContentCopyIcon />
                                    </button>
                                    <button
                                        className="editor-close-button editor-delete-button"
                                        onClick={() => activeWidgetId && deleteWidget(activeWidgetId)}
                                        title="Verwijderen"
                                        disabled={!activeWidget || activeWidget.parent === null}
                                    >
                                        <DeleteIcon />
                                    </button>
                                    <button 
                                        className="editor-close-button"
                                        onClick={() => {
                                            setActiveWidgetId(null);
                                        }}
                                        title="Sluiten"
                                    >
                                        <CloseIcon />
                                    </button>
                                </div>
                            </div>
                            <WidgetControlsPanel
                                key={activeWidget.id}
                                widget={activeWidget}
                                flashToken={controlsFlashToken}
                                onChange={handleControlChange}
                            />
                        </div>
                    )}

                </div>
            </div>

            {/* Editor area / Canvas */}
            <div className="report-builder-canvas-area" ref={canvasAreaRef}>
                <Canvas
                    widgets={widgets}
                    rootNode={rootNode}
                    editable={true}
                />
            </div>

            <ContextMenu>
                <ContextMenu.Button 
                    label="Bewerken" 
                    onClick={clickedWidgetId => setActiveWidgetId(clickedWidgetId)} 
                    // disabled={() => !activeWidgetId} 
                />
                <ContextMenu.Divider />
                <ContextMenu.Button
                    label="Stijl kopiëren"
                    onClick={clickedWidgetId => {
                        const widget = getWidgetById(clickedWidgetId);
                        if (!widget) return;

                        const allowedProperties = [
                            'color', 
                            'backgroundColor',
                            'fontSize',
                            'fontWeight',
                            'textAlign',
                            'font',
                            'flex',
                            'background',
                            'backgroundImage',
                            'border',
                            'borderColor',
                            'borderWidth',
                            'borderRadius',
                        ];

                        localStorage.setItem('copiedWidget', JSON.stringify({
                            type: widget.constructor.name,
                            settings: Object.fromEntries(Object.entries(widget.settings).filter(([key]) => allowedProperties.includes(key)))
                        }));
                    }}
                    disabled={clickedWidgetId => {
                        const widget = getWidgetById(clickedWidgetId);
                        return !widget;
                    }}
                />
                <ContextMenu.Button
                    label="Stijl plakken"
                    onClick={clickedWidgetId => {
                        const widget = getWidgetById(clickedWidgetId);
                        const copiedData = localStorage.getItem('copiedWidget');
                        if (!widget || !copiedData) return;

                        try {
                            const { type, settings } = JSON.parse(copiedData);
                            // if (type !== widget.constructor.name) {
                            //     alert('Gekopieerde stijl is van een ander type widget');
                            //     return;
                            // }
                            Object.entries(settings).forEach(([key, value]) => {
                                widget.setSetting(key, value);
                            });
                            setWidgets([...widgets]); // Trigger re-render
                            scheduleSave && scheduleSave(); // Persist pasted style
                        } catch (error) {
                            console.error('Failed to parse copied widget data:', error);
                        }
                    }}
                />
                <ContextMenu.Divider />
                <ContextMenu.Button 
                    label="Widget verwijderen" 
                    onClick={clickedWidgetId => clickedWidgetId && deleteWidget(clickedWidgetId)} 
                    disabled={clickedWidgetId => {
                        const widget = getWidgetById(clickedWidgetId);
                        return !widget || widget.parent === null;
                    }} 
                />
            </ContextMenu>
        </div>
    )
}
